import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { CalendarBlank, DownloadSimple, TrendUp } from "@phosphor-icons/react";
import PageShell from "@/components/dashboard/PageShell";
import { api, endpoints } from "@/lib/api";
import { PAGES, DASHBOARD } from "@/constants/testIds";

const Report = () => {
    const [report, setReport] = useState(null);
    const [user, setUser] = useState(null);
    const [community, setCommunity] = useState(null);

    useEffect(() => {
        let ignore = false;
        Promise.all([
            api.get(endpoints.annualReport()),
            api.get(endpoints.userImpact()),
            api.get(endpoints.community()),
        ])
            .then(([r, u, c]) => {
                if (ignore) return;
                setReport(r.data);
                setUser(u.data);
                setCommunity(c.data);
            })
            .catch(() => {});
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <PageShell testId={PAGES.report}>
            <div className="flex flex-col gap-6 sm:gap-8">
                {/* Hero */}
                <section
                    data-testid={DASHBOARD.annualReport}
                    className="relative overflow-hidden rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:p-10"
                >
                    <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-[#a3e635]/10 blur-3xl" />
                    <div className="relative">
                        {report && (
                            <span className="eviq-chip">
                                <CalendarBlank size={12} color="#a3e635" weight="fill" />
                                Arrives{" "}
                                {new Date(report.ready_on).toLocaleDateString(undefined, {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                })}
                            </span>
                        )}
                        <h1 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                            {report?.headline || "Your EVIQ Earth Report"}
                        </h1>
                        <p className="mt-4 max-w-2xl text-sm text-zinc-400 lg:text-base">
                            Every January, every EVIQ member receives a personalized summary of
                            battery health, resource savings, and the community projects their
                            membership helped power. This is the live preview.
                        </p>
                        <button
                            type="button"
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-5 py-2.5 text-sm text-[#a3e635] transition-colors duration-200 hover:bg-[#141a09]"
                        >
                            <DownloadSimple size={14} weight="bold" />
                            Preview last year's report (PDF)
                        </button>
                    </div>
                </section>

                {/* Your year */}
                {report && (
                    <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                        <div className="mb-6 flex items-center gap-2">
                            <TrendUp size={16} color="#a3e635" weight="fill" />
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                Your year · {report.year}
                            </span>
                        </div>
                        <h2 className="font-editorial text-2xl text-white sm:text-3xl">
                            Personal impact, in receipts.
                        </h2>
                        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                            {[
                                {
                                    label: "Battery health change",
                                    value: `+${report.battery_health_change_pct}%`,
                                    tone: "text-[#a3e635]",
                                },
                                {
                                    label: "Trees this year",
                                    value: report.trees_this_year,
                                    tone: "text-white",
                                },
                                {
                                    label: "Ocean plastic",
                                    value: `${report.ocean_plastic_lbs_this_year} lbs`,
                                    tone: "text-white",
                                },
                                {
                                    label: "Veteran hours funded",
                                    value: report.veteran_hours_funded,
                                    tone: "text-[#f59e0b]",
                                },
                            ].map((m) => (
                                <div
                                    key={m.label}
                                    className="rounded-md border border-[#1f1f22] bg-black/50 p-4 backdrop-blur"
                                >
                                    <div className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                        {m.label}
                                    </div>
                                    <div
                                        className={`mt-2 font-mono-metric text-2xl font-bold sm:text-3xl ${m.tone}`}
                                    >
                                        {m.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 rounded-md border border-[#1f1f22] bg-black/50 p-4 backdrop-blur">
                            <div className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                Highlight
                            </div>
                            <p className="mt-2 font-editorial text-lg italic leading-snug text-zinc-200">
                                "{report.highlight}"
                            </p>
                        </div>
                    </section>
                )}

                {/* Community aggregate */}
                {community && (
                    <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                        <span className="eviq-chip">Community aggregate · Live</span>
                        <h2 className="mt-4 font-editorial text-2xl text-white sm:text-3xl">
                            What every EVIQ member built, together.
                        </h2>
                        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                            {[
                                { label: "Trees restored", value: community.trees_restored, color: "#a3e635" },
                                { label: "Ocean plastic (lbs)", value: community.ocean_plastic_lbs, color: "#06b6d4" },
                                { label: "Batteries diverted", value: community.batteries_diverted, color: "#8b5cf6" },
                                { label: "CO₂ prevented (lbs)", value: community.co2_prevented_lbs, color: "#a3e635" },
                                { label: "Veterans sponsored", value: community.veterans_sponsored, color: "#f59e0b" },
                                { label: "Schools supported", value: community.schools_supported, color: "#f59e0b" },
                                { label: "Acres restored", value: community.acres_restored, color: "#a3e635" },
                                {
                                    label: "Battery-years extended",
                                    value: community.battery_life_extended_years,
                                    color: "#06b6d4",
                                },
                            ].map((m) => (
                                <div
                                    key={m.label}
                                    className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-4"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="h-2 w-2 rounded-full"
                                            style={{ background: m.color }}
                                        />
                                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                            {m.label}
                                        </span>
                                    </div>
                                    <div className="mt-2 font-mono-metric text-2xl font-bold text-white">
                                        <CountUp end={m.value} separator="," duration={2} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Member footer */}
                {user && (
                    <footer className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 text-center sm:p-8">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                            {user.founder_tier_label} · Member No. {user.member_number}
                        </span>
                        <p className="mx-auto mt-3 max-w-2xl font-editorial text-lg italic text-zinc-400">
                            Prepared for {user.display_name}. Certified by the EVIQ Earth
                            Initiative.
                        </p>
                    </footer>
                )}
            </div>
        </PageShell>
    );
};

export default Report;
